import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import StatusDropdown from './StatusDropdown';
import PriorityDropdown from './PriorityDropdown';
import AssigneePicker from './AssigneePicker';
import DependencyPicker from './DependencyPicker';
import SubtasksManager from './SubtasksManager';
import PaymentsSection from './PaymentsSection';
import './PaymentsSection.css';
import './EditTaskModalFinance.css';

const CURRENCIES = ['USD', 'EUR', 'TRY', 'AED', 'SAR', 'GBP', 'JPY', 'CNY', 'INR'];

const EditTaskModal = ({ isOpen, onClose, task, token, onUpdate, boardId, boardBaseCurrency = 'USD', initialTab = 'details' }) => {
  const [formData, setFormData] = useState(task || {});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab); // 'details' | 'finance'

  useEffect(() => {
    if (isOpen && task) {
      setFormData({ 
        ...task, 
        subtasks: task.subtasks || [],
        dependencies: task.dependencies || [],
        value: task.value ?? 0,
        currency: task.currency || boardBaseCurrency,
        exchangeRate: task.exchangeRate ?? 1,
      });
      setActiveTab(initialTab || 'details');
    }
  }, [isOpen, task, boardBaseCurrency, initialTab]);

  if (!isOpen || !task) return null;

  const handleUpdate = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/items/${task.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          status: formData.status,
          priority: formData.priority,
          assignees: formData.assignees,
          startDate: formData.startDate,
          dueDate: formData.dueDate,
          progress: formData.progress,
          dependencies: formData.dependencies,
          subtasks: formData.subtasks,
          fileLinks: formData.fileLinks,
          value: parseFloat(formData.value) || 0,
          currency: formData.currency || 'USD',
          exchangeRate: parseFloat(formData.exchangeRate) || 1,
        })
      });
      
      if (response.ok) {
        onUpdate();
        onClose();
      }
    } catch (error) {
      console.error('Failed to update task:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    return dateStr.split('T')[0];
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '760px' }}>
        <div className="modal-header">
          <h2 className="modal-title">Edit Task</h2>
          <button className="modal-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid var(--border-light)', marginBottom: 16 }}>
          <button
            className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
            data-testid="task-details-tab"
          >
            Details
          </button>
          <button
            className={`tab-btn ${activeTab === 'finance' ? 'active' : ''}`}
            onClick={() => setActiveTab('finance')}
            data-testid="task-finance-tab"
          >
            Finance
          </button>
        </div>

        <div className="modal-body">
          {activeTab === 'details' && (
            <>
              <div className="form-group">
                <label className="form-label">Task Name</label>
                <input
                  type="text"
                  className="input"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="edit-task-name"
                />
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <StatusDropdown
                    value={formData.status}
                    onChange={(newStatus) => setFormData({ ...formData, status: newStatus })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <PriorityDropdown
                    value={formData.priority}
                    onChange={(newPriority) => setFormData({ ...formData, priority: newPriority })}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Assignees</label>
                <AssigneePicker
                  assignees={formData.assignees || []}
                  onChange={(assignees) => setFormData({ ...formData, assignees })}
                  token={token}
                />
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    className="input"
                    value={formatDateForInput(formData.startDate)}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value ? new Date(e.target.value).toISOString() : null })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Due Date</label>
                  <input
                    type="date"
                    className="input"
                    value={formatDateForInput(formData.dueDate)}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value ? new Date(e.target.value).toISOString() : null })}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Progress (%)</label>
                <input
                  type="number"
                  className="input"
                  value={formData.progress || 0}
                  onChange={(e) => setFormData({ ...formData, progress: parseInt(e.target.value) || 0 })}
                  min="0"
                  max="100"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Dependencies</label>
                <DependencyPicker
                  dependencies={formData.dependencies || []}
                  onChange={(deps) => setFormData({ ...formData, dependencies: deps })}
                  token={token}
                  currentTaskId={task?.id}
                  boardId={boardId}
                />
                <p className="form-helper">Select tasks that must be completed before this task can start</p>
              </div>
              
              <div className="form-group">
                <SubtasksManager
                  subtasks={formData.subtasks || []}
                  onChange={(subtasks) => setFormData({ ...formData, subtasks })}
                  token={token}
                  editMode={true}
                />
              </div>
            </>
          )}

          {activeTab === 'finance' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Client Price</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input"
                    value={formData.value || 0}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    placeholder="0.00"
                    data-testid="task-value-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Currency</label>
                  <select
                    className="input"
                    value={formData.currency || boardBaseCurrency}
                    onChange={(e) => {
                      const next = e.target.value;
                      setFormData({ ...formData, currency: next, exchangeRate: next === boardBaseCurrency ? 1 : (formData.exchangeRate || 1) });
                    }}
                    data-testid="task-currency-select"
                  >
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    {formData.currency && !CURRENCIES.includes(formData.currency) && (
                      <option value={formData.currency}>{formData.currency}</option>
                    )}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    {formData.currency === boardBaseCurrency
                      ? 'Rate (same currency)'
                      : `1 ${boardBaseCurrency} = ? ${formData.currency || boardBaseCurrency}`}
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    className="input"
                    value={formData.exchangeRate || 1}
                    onChange={(e) => setFormData({ ...formData, exchangeRate: e.target.value })}
                    disabled={formData.currency === boardBaseCurrency}
                  />
                  {formData.currency !== boardBaseCurrency && formData.value > 0 && formData.exchangeRate > 0 && (
                    <p className="form-helper">
                      ≈ <strong>{(parseFloat(formData.value) / parseFloat(formData.exchangeRate)).toLocaleString(undefined, { maximumFractionDigits: 2 })} {boardBaseCurrency}</strong>
                    </p>
                  )}
                </div>
              </div>

              <button
                className="btn btn-primary btn-sm"
                onClick={handleUpdate}
                disabled={loading}
                data-testid="save-task-value-btn"
                style={{ marginBottom: 12 }}
              >
                {loading ? 'Saving...' : 'Save Client Price & Currency'}
              </button>

              <PaymentsSection
                taskId={task.id}
                token={token}
                taskCurrency={formData.currency || boardBaseCurrency}
                taskValue={parseFloat(formData.value) || 0}
                boardBaseCurrency={boardBaseCurrency}
                onChanged={onUpdate}
              />

              <div className="task-finance-help" style={{ marginTop: 12 }}>
                <strong>Note:</strong> This Board shows <em>client-visible amounts only</em>.
                Payments here track the contractor amounts that <em>can be shown to the client</em>
                (not your internal subcontractor costs). Client payments received are managed on
                the board's <strong>Client Statement → Payments Received</strong> section.
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          {activeTab === 'details' && (
            <button className="btn btn-primary" onClick={handleUpdate} disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditTaskModal;
